import {useState, type ReactNode} from 'react';
import {Frame, Navigation, TopBar} from '@shopify/polaris';
import type {IconSource} from '@shopify/polaris';

/**
 * The shared app shell for every *.slj.app surface: Polaris Frame + TopBar
 * (S-mark logo, user menu) + left Navigation. One component serves both
 * consumption modes — Navigation/UserMenu links render through the AppProvider
 * linkComponent, so React Router apps get client-side nav and Hono apps get
 * plain anchors.
 *
 * SSR-safe under whole-document hydration: every piece of interactive state
 * starts false on both sides (Polaris's own breakpoint/media-query hooks also
 * default false server-side and correct in effects). Icons therefore CANNOT
 * come from route-handler props (window.__PAGE__ is JSON) — sections are built
 * in view-side modules on both sides of the hydrate.
 */

export interface ChromeUser {
  name: string;
  email: string;
  avatarUrl: string | null;
  isAdmin: boolean;
}

export interface NavItemDef {
  label: string;
  url: string;
  icon: IconSource;
}

export interface NavSectionDef {
  title?: string;
  items: NavItemDef[];
}

export interface AccountUrls {
  settingsUrl: string;
  signOutUrl: string;
}

/**
 * Which nav item is highlighted for a given path.
 *
 * Exact match, or a path boundary below it — and of the items that match, only the
 * LONGEST url wins, so /settings/security selects Security rather than both Security and
 * Settings, and /menus/:id selects Menus.
 *
 * The last resort is the longest plain STRING PREFIX, and it is a deliberate compromise
 * with a sharp edge: it exists so a page with no nav item of its own highlights the item
 * it is a sibling of, but string prefixes do not respect path segments. `/admin/import`
 * is a prefix of `/admin/imports`, so while Import History had no nav entry it lit up
 * Import instead — the right fix was to give the page an entry (a boundary match then
 * wins outright), not to sand off the fallback.
 *
 * Exported and tested because it has now been reasoned about twice and got it wrong once.
 */
export function selectNavUrl(sections: NavSectionDef[], currentPath: string): string | undefined {
  const urls = sections.flatMap((section) => section.items).map((item) => item.url);
  const onBoundary = (url: string) => currentPath === url || currentPath.startsWith(`${url}/`);
  const longest = (candidates: string[]) => candidates.sort((a, b) => b.length - a.length)[0];
  return longest(urls.filter(onBoundary)) ?? longest(urls.filter((url) => currentPath.startsWith(url)));
}

export function AppFrame({
  logoUrl,
  logoSuffix,
  user,
  sections,
  currentPath,
  account,
  children,
}: {
  logoUrl: string;
  logoSuffix?: ReactNode;
  user: ChromeUser;
  sections: NavSectionDef[];
  currentPath: string;
  account: AccountUrls;
  children?: ReactNode;
}) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const selectedUrl = selectNavUrl(sections, currentPath);

  // Sign-out is a POST (a GET is CSRF-triggerable — an <img> could log you out).
  // The menu action submits a hidden same-origin form; document is reached via a
  // cast because this component also renders under workerd (no DOM lib).
  const submitSignOut = () =>
    (globalThis as {document?: {getElementById(id: string): {submit(): void} | null}}).document
      ?.getElementById('slj-sign-out')
      ?.submit();

  const topBar = (
    <TopBar
      showNavigationToggle
      logoSuffix={logoSuffix}
      userMenu={
        <TopBar.UserMenu
          actions={[
            {items: [{content: 'Account settings', url: account.settingsUrl}]},
            {items: [{content: 'Sign out', onAction: submitSignOut}]},
          ]}
          name={user.name}
          detail={user.email}
          avatar={user.avatarUrl ?? undefined}
          initials={user.name.slice(0, 1).toUpperCase()}
          open={userMenuOpen}
          onToggle={() => setUserMenuOpen((open) => !open)}
        />
      }
      onNavigationToggle={() => setMobileNavOpen((open) => !open)}
    />
  );

  const navigation = (
    <Navigation
      location={currentPath}
      logoSuffix={logoSuffix}
      onDismiss={() => setMobileNavOpen(false)}
    >
      {sections.map((section, index) => (
        <Navigation.Section
          key={section.title ?? `section-${index}`}
          title={section.title}
          items={section.items.map((item) => ({
            label: item.label,
            url: item.url,
            icon: item.icon,
            selected: item.url === selectedUrl,
          }))}
        />
      ))}
    </Navigation>
  );

  return (
    <Frame
      logo={{
        topBarSource: logoUrl,
        url: '/',
        accessibilityLabel: 'slj.app',
        width: 32,
      }}
      topBar={topBar}
      navigation={navigation}
      showMobileNavigation={mobileNavOpen}
      onNavigationDismiss={() => setMobileNavOpen(false)}
    >
      {/* Same-origin POST target for the user-menu Sign out action. */}
      <form id="slj-sign-out" method="post" action={account.signOutUrl} hidden />
      {children}
    </Frame>
  );
}
